/**
 * MainPolicies Component
 *
 * Layout and router for the /policies section. Renders a two-column layout
 * with a left-hand sidebar navigation and a right-hand content area that
 * displays the selected policy via nested routes.
 *
 * Policies are a mix of custom components (AcceptableUse, DataRetention)
 * and Termly-hosted iframes (Privacy, Cookie, Terms, Accessibility).
 * The PoliciesIndex sub-component serves as the landing page with summary
 * cards linking to each individual policy.
 *
 * Internal helper components:
 *   - TermlyEmbed: Renders an iframe for externally hosted Termly policies
 *   - PrivacyPolicy, CookiePolicy, TermsOfService, AccessibilityStatement:
 *       Thin wrappers that pair a Helmet title with a TermlyEmbed
 *   - PoliciesIndex: Overview page with card grid linking to all policies
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Nav, Card } from 'react-bootstrap';

import SEO from '@/components/common/SEO';

import AcceptableUse from './AcceptableUse';
import DataRetention from './DataRetention';
import './Policies.css';

// Termly-hosted policy URLs: used by the TermlyEmbed iframe wrapper
const TERMLY_POLICY_URLS = {
    privacy:
        'https://app.termly.io/policy-viewer/policy.html?policyUUID=a0a68fe9-1756-4c59-82bc-c024c4ffc582',
    cookiePolicy:
        'https://app.termly.io/policy-viewer/policy.html?policyUUID=a7b2aec3-1952-478d-9a89-9d23230145a1',
    terms: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=8e39633a-d1ac-4e2c-8df1-9c7a0fcb29e6',
    accessibility:
        'https://app.termly.io/policy-viewer/policy.html?policyUUID=2322d150-81a3-475d-9f6c-135fbe8814a3',
};

function TermlyEmbed({ title, url }) {
    return <iframe src={url} title={title} className="termly-embed" />;
}

TermlyEmbed.propTypes = {
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
};

function PrivacyPolicy() {
    return (
        <>
            <SEO
                title="Privacy Policy"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    { name: 'Privacy Policy', path: '/policies/privacy' },
                ]}
            />
            <TermlyEmbed
                title="Privacy Policy"
                url={TERMLY_POLICY_URLS.privacy}
            />
        </>
    );
}

function CookiePolicy() {
    return (
        <>
            <SEO
                title="Cookie Policy"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    { name: 'Cookie Policy', path: '/policies/cookie-policy' },
                ]}
            />
            {TERMLY_POLICY_URLS.cookiePolicy ? (
                <TermlyEmbed
                    title="Cookie Policy"
                    url={TERMLY_POLICY_URLS.cookiePolicy}
                />
            ) : (
                <>
                    <h2 className="text-primary mb-4">Cookie Policy</h2>
                    <p className="text-muted">Coming soon.</p>
                </>
            )}
        </>
    );
}

function TermsOfService() {
    return (
        <>
            <SEO
                title="Terms of Service"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    { name: 'Terms of Service', path: '/policies/terms' },
                ]}
            />
            <TermlyEmbed
                title="Terms of Service"
                url={TERMLY_POLICY_URLS.terms}
            />
        </>
    );
}

function AccessibilityStatement() {
    return (
        <>
            <SEO
                title="Accessibility"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    {
                        name: 'Accessibility',
                        path: '/policies/accessibility',
                    },
                ]}
            />
            <TermlyEmbed
                title="Accessibility Statement"
                url={TERMLY_POLICY_URLS.accessibility}
            />
        </>
    );
}

/**
 * PoliciesIndex: Landing page for /policies showing summary cards
 * for each policy with links to individual policy pages.
 */
function PoliciesIndex() {
    return (
        <>
            <SEO
                title="Policies"
                description="Review RILE Connect policies: privacy, cookies, terms of service, accessibility, acceptable use, and data retention."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                ]}
            />
            <h2 className="text-primary mb-4">Policies</h2>
            <p className="mb-4">
                Please review our policies to understand how RILE Connect
                operates.
            </p>

            {/* Policy summary cards: one per policy */}
            <Row className="g-4">
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Privacy Policy</h5>
                            <p className="text-muted small">
                                How we collect, use, and protect your
                                information.
                            </p>
                            <Link to="/policies/privacy">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Cookie Policy</h5>
                            <p className="text-muted small">
                                How we use cookies and tracking technologies.
                            </p>
                            <Link to="/policies/cookie-policy">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Terms of Service</h5>
                            <p className="text-muted small">
                                The rules and guidelines for using RILE Connect.
                            </p>
                            <Link to="/policies/terms">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Accessibility</h5>
                            <p className="text-muted small">
                                Our commitment to accessible design.
                            </p>
                            <Link to="/policies/accessibility">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Acceptable Use</h5>
                            <p className="text-muted small">
                                Rules for using the platform, directory, and AI
                                assistant.
                            </p>
                            <Link to="/policies/acceptable-use">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                            <h5 className="text-primary">Data Retention</h5>
                            <p className="text-muted small">
                                How long we keep your data and how to request
                                deletion.
                            </p>
                            <Link to="/policies/data-retention">Read More</Link>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default function MainPolicies() {
    const location = useLocation();

    return (
        <Container className="py-5">
            <Row>
                {/* Sidebar navigation: highlights the active policy route */}
                <Col md={3}>
                    <Nav className="flex-column policy-nav mb-4">
                        <Nav.Link
                            as={Link}
                            to="/policies"
                            className={
                                location.pathname === '/policies'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Overview
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/privacy"
                            className={
                                location.pathname === '/policies/privacy'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Privacy Policy
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/cookie-policy"
                            className={
                                location.pathname === '/policies/cookie-policy'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Cookie Policy
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/terms"
                            className={
                                location.pathname === '/policies/terms'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Terms of Service
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/accessibility"
                            className={
                                location.pathname === '/policies/accessibility'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Accessibility
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/acceptable-use"
                            className={
                                location.pathname === '/policies/acceptable-use'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Acceptable Use
                        </Nav.Link>
                        <Nav.Link
                            as={Link}
                            to="/policies/data-retention"
                            className={
                                location.pathname === '/policies/data-retention'
                                    ? 'active'
                                    : ''
                            }
                        >
                            Data Retention
                        </Nav.Link>
                    </Nav>
                </Col>
                {/* Policy content area: renders the matched nested route */}
                <Col md={9}>
                    <Routes>
                        <Route path="/" element={<PoliciesIndex />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route
                            path="/cookie-policy"
                            element={<CookiePolicy />}
                        />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route
                            path="/accessibility"
                            element={<AccessibilityStatement />}
                        />
                        <Route
                            path="/acceptable-use"
                            element={<AcceptableUse />}
                        />
                        <Route
                            path="/data-retention"
                            element={<DataRetention />}
                        />
                    </Routes>
                </Col>
            </Row>
        </Container>
    );
}
